import { StripePriceLookupKey } from '@paragon/contract-library';

import { designerPlanColor, estimatorPlanColor, freePlanColor } from './colors';

export interface Plan {
  readonly stripePriceLookupKeyMonthly: StripePriceLookupKey | null;
  readonly stripePriceLookupKeyYearly: StripePriceLookupKey | null;
  readonly name: string;
  readonly description: string;
  readonly color: string;
  readonly monthlyPrice: number;
  readonly yearlyPrice: number;
  readonly features: FeatureList;
}

type FeatureColor = typeof freePlanColor | typeof estimatorPlanColor | typeof designerPlanColor;
type FeatureList = [feature: string, color: FeatureColor][];

// Feature lists duplicated from https://paragontruss.com/pricing
const FREE_PLAN_FEATURES: FeatureList = [
  ['1 User', freePlanColor],
  ['Paragon Truss Link with TDD', freePlanColor],
  ['Live and Dead Loading Analysis', freePlanColor],
  ['Includes Basic Support', freePlanColor],
  ['View-Only Layouts', freePlanColor],
  ['5 Prototype Trusses', freePlanColor],
];

export const FREE_PLAN: Plan = {
  stripePriceLookupKeyMonthly: null,
  stripePriceLookupKeyYearly: null,
  name: 'Free Plan',
  description: 'For simple testing and use',
  color: freePlanColor,
  monthlyPrice: 0,
  yearlyPrice: 0,
  features: FREE_PLAN_FEATURES,
};

const INDIVIDUAL_ESTIMATOR_PLAN_FEATURES: FeatureList = [
  ['1 User', freePlanColor],
  ['Paragon Truss Link with TDD', freePlanColor],
  ['Unplated Truss Design and Analysis', estimatorPlanColor],
  ['Includes Standard Support', estimatorPlanColor],
  ['3D Layout Tools', estimatorPlanColor],
  ['250 Prototype Trusses Per Month', estimatorPlanColor],
  ['$0.25 Per Prototype Truss after Quota', estimatorPlanColor],
  ['Access to RESTful API', estimatorPlanColor],
  ['Estimation Download Options', estimatorPlanColor],
  ['Carried Component Connections', estimatorPlanColor],
  ['Price Management', estimatorPlanColor],
  ['Lumber and Connector Lists', estimatorPlanColor],
];

export const INDIVIDUAL_ESTIMATOR_PLAN: Plan = {
  stripePriceLookupKeyMonthly: 'individual-estimator-monthly',
  stripePriceLookupKeyYearly: 'individual-estimator-yearly',
  name: 'Estimator Plan',
  description: 'For estimation and modeling professionals',
  color: estimatorPlanColor,
  monthlyPrice: 100,
  yearlyPrice: 1000,
  features: INDIVIDUAL_ESTIMATOR_PLAN_FEATURES,
};

const INDIVIDUAL_DESIGNER_PLAN_FEATURES: FeatureList = [
  ['1 User', freePlanColor],
  ['Paragon Truss Link with TDD', freePlanColor],
  ['Full Plated Truss Design and Analysis', designerPlanColor],
  ['Includes Standard Support', estimatorPlanColor],
  ['3D Layout Tools', estimatorPlanColor],
  ['250 Manufacturer Trusses Per Month', designerPlanColor],
  ['$2 Per Manufacturer Truss after Quota', designerPlanColor],
  ['Access to RESTful API', estimatorPlanColor],
  ['Premium Download Options', designerPlanColor],
  ['Carried Component Connections', estimatorPlanColor],
  ['Price Management', estimatorPlanColor],
  ['Lumber, Plate, and Connector Lists', designerPlanColor],
  ['Access to Engineer Network', designerPlanColor],
];

export const INDIVIDUAL_DESIGNER_PLAN: Plan = {
  stripePriceLookupKeyMonthly: 'individual-designer-monthly',
  stripePriceLookupKeyYearly: 'individual-designer-yearly',
  name: 'Designer Plan',
  description: 'For component manufacturer designers',
  color: designerPlanColor,
  monthlyPrice: 250,
  yearlyPrice: 2500,
  features: INDIVIDUAL_DESIGNER_PLAN_FEATURES,
};
